import { skipToken, useQuery } from '@tanstack/react-query';

import { Boards, type GetBoardParams } from '@air/api';
import { FIVE_MINUTES } from '@air/utilities';

import { isWorkspaceAgentPillBoardUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentPillBoardUnresolvableError';
import { getPrivateBoardKey } from '~/components/v2/hooks/queries/usePrivateBoard';

/**
 * Ancestors for the location line, cover for the preview, one thumbnail as its fallback, and the direct
 * descendant count for the item line — sub-boards included, which the deprecated stats endpoint omits.
 */
const WORKSPACE_AGENT_PILL_BOARD_PARAMS = {
  includeAncestors: true,
  includeCoverImage: true,
  includeDirectDescendantCount: true,
  numThumbnails: 1,
} as const;

const PILL_BOARD_TRANSIENT_RETRIES = 1;

export type UsePrivateWorkspaceAgentPillBoardParams = {
  /** A pill that carries no board never fetches. */
  boardId: GetBoardParams['boardId'] | undefined;
};

export type UsePrivateWorkspaceAgentPillBoardOptions = {
  /** Gated on the chip being in view, so a long history does not fan out a request per chip. */
  enabled?: boolean;
};

/**
 * Built with `getPrivateBoardKey`, so a chip sits in the same key namespace as a board page but keeps its
 * own entry — the chip's params (ancestors, cover, descendant count) differ from the page's. `throwOnError:
 * false`: an unresolvable mention renders an unavailable chip, not an error boundary.
 */
export const usePrivateWorkspaceAgentPillBoard = (
  { boardId }: UsePrivateWorkspaceAgentPillBoardParams,
  options?: UsePrivateWorkspaceAgentPillBoardOptions,
) =>
  useQuery({
    queryKey: getPrivateBoardKey({ ...WORKSPACE_AGENT_PILL_BOARD_PARAMS, boardId }),
    queryFn: boardId ? () => Boards.getWithNoWorkspaceId({ ...WORKSPACE_AGENT_PILL_BOARD_PARAMS, boardId }) : skipToken,
    enabled: options?.enabled ?? true,
    // Boards are renamed far less often than history is read.
    staleTime: FIVE_MINUTES,
    // A deleted or unreachable board reads the same on every attempt; anything else is a blip.
    retry: (failureCount, error) =>
      !isWorkspaceAgentPillBoardUnresolvableError(error) && failureCount < PILL_BOARD_TRANSIENT_RETRIES,
    // Only worth re-reading on focus when the chip is currently showing an error.
    refetchOnWindowFocus: (query) => !!query.state.error,
    throwOnError: false,
  });
